import React, {useMemo} from 'react';

import {MainColumnCell, MainColumnHeader, getPaginatedRowNumber} from 'src/components/main-column';
import {useDataTableColumnsWidthOnResize} from 'src/hooks/useDataTableColumnsWidthOnResize';

import {
    Cell,
    type CellOptions,
    Column, DataTable,
    HeaderCellOptions,
    type RowsParams,
    TableHeader,
} from '@pb/kit-component-data-table-v2';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';


import {
    ActionDescriptionCell,
    ActionInitiatorCell,
    ActionTimestampCell,
    ActionTypeCell,
} from './cells';
import {COLUMNS, COLUMNS_DATA, HEADERS} from './constants';
import {EntitlementGrantChangeLogPagination} from './entitlement-grant-сhange-log-pagination';

import type {DictionaryItem} from 'src/openapi/admin/models/DictionaryItem';
import type {LogHits} from 'src/openapi/admin/models/LogHits';
import type {Pagination} from 'src/openapi/admin/models/Pagination';

type Props = {
    rows: LogHits[],
    totalCount: number,
    pagination: Pagination,
    actionTypeDictionary: DictionaryItem[],
};

export const ChangeLogTable = ({rows, totalCount, pagination, actionTypeDictionary}: Props) => {
    const {getColumnWidthByColumnId, onStopResize} = useDataTableColumnsWidthOnResize();

    const renderedRows = useMemo(() => (rows
        .map(log => ({
            id: log.recordId,
            columnValues: {
                actionName: log.actionName,
                actionDescription: log.actionDescription,
                initiator: {
                    user: log.initiatorUser,
                    system: log.initiatorSystem,
                    process: log.initiatorSystemProcess,
                },
                timestamp: log.timestamp,
                reason: log.reason,
            },
        }))
    ), [rows]);

    const maxRowNumber = getPaginatedRowNumber({...pagination, index: renderedRows?.length});

    return (
        <DataTable
            id="accont-entitlements-change-log-table"
            data={{
                rows: renderedRows, columns: COLUMNS_DATA.map((column: Column) => ({
                    ...column,
                    width: getColumnWidthByColumnId(column.columnId),
                })),
            }}
            getHeaderTopOffset={() => 221}
            onStopResize={onStopResize}
            headers={({column}: {column: Column}, cellOptions: HeaderCellOptions) => {
                switch (column.columnId) {
                    case COLUMNS.ACTION_NAME:
                        return (
                            <Cell
                                {...cellOptions}
                                key={`${column.columnId}`}
                                column={column}
                                resizable={true}>
                                <MainColumnHeader
                                    selection={<div/>}
                                    maxRowNumber={maxRowNumber}>
                                    <strong>{HEADERS[column.columnId]}</strong>
                                </MainColumnHeader>
                            </Cell>
                        );
                    default:
                        return (
                            <Cell
                                {...cellOptions}
                                key={`${column.columnId}`}
                                column={column}
                                resizable={true}>
                                <TableHeader indent="none" className="search-header">
                                    <strong>{HEADERS[column.columnId]}</strong>
                                </TableHeader>
                            </Cell>
                        );
                }
            }}
            renderRows={(rowsParams: RowsParams, cellOptions: CellOptions) => {
                const {rowIndex, column: {columnId}} = rowsParams;
                const value = rowsParams.row.columnValues[columnId];
                const column = rowsParams.column;
                const rowId = +rowsParams.row.id;
                const cellKey = `${rowId}-${columnId}`;
                const rowNumber = getPaginatedRowNumber({
                    ...pagination,
                    index: rowIndex + 1,
                });

                switch (columnId) {
                    case COLUMNS.ACTION_NAME:
                        return (
                            <MainColumnCell
                                rowId={rowId}
                                key={cellKey}
                                selection={<div/>}
                                rowNumber={rowNumber}
                                maxRowNumber={maxRowNumber}
                                cellOptions={{...cellOptions, column}}>
                                <ActionTypeCell
                                    actionTypeCode={value as string}
                                    actionTypeDictionary={actionTypeDictionary}
                                />
                            </MainColumnCell>
                        );
                    case COLUMNS.TIMESTAMP:
                        return (
                            <Cell
                                {...cellOptions}
                                key={cellKey}
                                column={column}
                                className="search-result-cell">
                                <ActionTimestampCell timestamp={value as string}/>
                            </Cell>
                        );
                    case COLUMNS.INITIATOR:
                        return (
                            <Cell
                                {...cellOptions}
                                key={cellKey}
                                column={column}
                                className="search-result-cell">
                                <ActionInitiatorCell {...(value as React.ComponentProps<typeof ActionInitiatorCell>)}/>
                            </Cell>
                        );
                    case COLUMNS.ACTION_DESCRIPTION:
                        return (
                            <Cell
                                {...cellOptions}
                                key={cellKey}
                                column={column}
                                className="search-result-cell">
                                <ActionDescriptionCell description={value as string}/>
                            </Cell>
                        );
                    default:
                        const displayValue = String(value);
                        return (
                            <Cell
                                {...cellOptions}
                                key={cellKey}
                                column={column}
                                className="search-result-cell">
                                <EllipsisTextHint title={displayValue}>
                                    {displayValue}
                                </EllipsisTextHint>
                            </Cell>
                        );
                }
            }}
            paginationStripe={<EntitlementGrantChangeLogPagination totalCount={totalCount}/>}
        />
    );
};
